import React, { useEffect, useState } from 'react';

import { useUserContext } from '../../context/UserStore';
import { BlurOnRounded, Difference } from '@mui/icons-material';
import DoneOutlineRoundedIcon from '@mui/icons-material/DoneOutlineRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import { Box, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import UpdateGoalsModal from '../modal/UpdateTasks';
import UpdateNotesModal from '../modal/UpdateNotes';
import AddTasksModal from '../modal/AddTasks';
import RemoveTasksModal from '../modal/RemoveTasks';
import moment from 'moment/moment';

// Goal/Task notification overlay with flipY animations for GoalAccordion
const GoalTaskNotificationOverlay = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.95)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
  
  '&.fade-out': {
    animation: 'fadeOut 1s ease-out forwards',
  },
  
  '@keyframes fadeOut': {
    '0%': {
      opacity: 1,
    },
    '100%': {
      opacity: 0,
      visibility: 'hidden',
    }
  }
}));

// Goal/Task notification message with flipY animations for GoalAccordion
const GoalTaskNotificationMessage = styled('h1')(({ theme }) => ({
  fontSize: '3.5rem',
  fontWeight: 'bold',
  color: 'white',
  textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
  margin: 0,
  animation: 'flipInY 1.5s ease-out, flipOutY 1.5s ease-in 2s forwards',
  
  '@keyframes flipInY': {
    '0%': {
      opacity: 0,
      transform: 'perspective(400px) rotateY(90deg)',
    },
    '40%': {
      transform: 'perspective(400px) rotateY(-20deg)',
    },
    '60%': {
      transform: 'perspective(400px) rotateY(10deg)',
    },
    '80%': {
      transform: 'perspective(400px) rotateY(-5deg)',
    },
    '100%': {
      opacity: 1,
      transform: 'perspective(400px) rotateY(0deg)',
    }
  },
  
  '@keyframes flipOutY': {
    '0%': {
      opacity: 1,
      transform: 'perspective(400px) rotateY(0deg)',
    },
    '30%': {
      transform: 'perspective(400px) rotateY(-20deg)',
    },
    '100%': {
      opacity: 0,
      transform: 'perspective(400px) rotateY(90deg)',
    }
  },
  
  // Responsive font sizes
  '@media (max-width: 768px)': {
    fontSize: '2.5rem',
  },
  '@media (max-width: 480px)': {
    fontSize: '2rem',
  }
}));

// Fast rainbow gradient accordion for current goal
const FastRainbowAccordion = styled(Accordion)(({ theme }) => ({
  background: 'linear-gradient(-45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4, #ffeaa7, #fab1a0, #fd79a8, #a29bfe)',
  backgroundSize: '400% 400%',
  animation: 'fastRainbow 3s ease infinite',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  
  '@keyframes fastRainbow': {
    '0%': {
      backgroundPosition: '0% 50%',
    },
    '50%': {
      backgroundPosition: '100% 50%',
    },
    '100%': {
      backgroundPosition: '0% 50%',
    }
  },
  
  // Green to dark cyan gradient animation
  '@keyframes greenToDarkBlue': {
    '0%': {
      backgroundPosition: '0% 50%',
    },
    '50%': {
      backgroundPosition: '100% 50%',
    },
    '100%': {
      backgroundPosition: '0% 50%',
    }
  },
  
  // Orange to yellow gradient animation
  '@keyframes orangeToYellow': {
    '0%': {
      backgroundPosition: '0% 50%',
    },
    '50%': {
      backgroundPosition: '100% 50%',
    },
    '100%': {
      backgroundPosition: '0% 50%',
    }
  },
  
  // Style text for better contrast
  '& .MuiAccordionSummary-content': {
    '& .MuiTypography-root': {
      color: 'white',
      fontWeight: 'bold',
      textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
    }
  },
  
  '& .MuiAccordionDetails-root': {
    // Orange to yellow animated gradient for interior
    background: 'linear-gradient(-45deg, #ff8c00, #ffa500, #ffb347, #ffd700)',
    backgroundSize: '400% 400%',
    animation: 'orangeToYellow 6s ease infinite',
    borderRadius: '0 0 8px 8px',
    color: 'black',
    fontWeight: 'bold',
    padding: '20px',
    
    // Add green to dark cyan gradient overlay for additional animation
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(-45deg, rgba(34, 197, 94, 0.3), rgba(0, 188, 212, 0.3), rgba(0, 96, 100, 0.3), rgba(15, 23, 42, 0.3))',
      backgroundSize: '400% 400%',
      animation: 'greenToDarkBlue 5s ease infinite',
      borderRadius: '0 0 8px 8px',
      pointerEvents: 'none',
    },
    
    // Ensure content appears above the overlay
    '& > *': {
      position: 'relative',
      zIndex: 1,
    }
  },
  
  '& .MuiSvgIcon-root': {
    color: 'white',
    filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.7))',
  }
}));

export const GoalAccordion = (props) => {
  const {notes,tasks,currentGoal,goalName} = props
  
  // Goal/Task notification state
  const [showGoalTaskNotification, setShowGoalTaskNotification] = useState(false);
  const [goalTaskNotificationMessage, setGoalTaskNotificationMessage] = useState('');
  const [fadeOutGoalTaskNotification, setFadeOutGoalTaskNotification] = useState(false);
  
  // Show goal/task notification function
  const showGoalTaskNotificationMessage = (message) => {
    setGoalTaskNotificationMessage(message);
    setShowGoalTaskNotification(true);
    setFadeOutGoalTaskNotification(false);

    // Start fade out after 3 seconds (after flip animations)
    const fadeTimer = setTimeout(() => {
      setFadeOutGoalTaskNotification(true);
    }, 3000);

    // Hide notification completely after fade out
    const hideTimer = setTimeout(() => {
      setShowGoalTaskNotification(false);
      setFadeOutGoalTaskNotification(false);
    }, 4000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  };

  const { state: userState, action: userActions } = useUserContext();
  const difference = moment().endOf('week').fromNow();
  const handleCompleteGoal = (item, index) => {
    let newTask = [...userState.currentGoal?.tasks]
    newTask[index] = {...item,done:!item.done}

    userActions.createGoalAsyncAction(userState.currentGoal.notes,newTask)
  };
  return (
    <>
      {/* Goal/Task Notification Overlay */}
      {showGoalTaskNotification && (
        <GoalTaskNotificationOverlay className={fadeOutGoalTaskNotification ? 'fade-out' : ''}>
          <GoalTaskNotificationMessage>{goalTaskNotificationMessage}</GoalTaskNotificationMessage>
        </GoalTaskNotificationOverlay>
      )}
      
      {currentGoal?
        <div>
          <FastRainbowAccordion defaultExpanded style={{margin:'auto'}}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
             Current Goal
            </AccordionSummary>
            <AccordionDetails>
              Goal for week {userState.currentGoal.week_number}
              <br />
              Goal ends {}
              <br />
              Notes:{notes || 'None'}
               <UpdateNotesModal/>
              <br />
              Task(s):
              <br/><br/>
              <div style={{display:'flex',flexDirection:'column'}} >
              {tasks?.map((item, index) => {
                return (
                  <div style={{display:'flex'}}>
                  <Box sx={{ boxShadow: 3 }} style={{display: 'flex', padding:'10px',alignItems:'center',margin:'10px',borderRadius:'50px'}}>

                    {item.name}
                    {item.optional?'':'*'}
                    {item.optional? <>&nbsp; &nbsp;<Chip label='optional' color="success" size="small" /></> : <>&nbsp; &nbsp;<Chip  label='required' color="error" size="small"/></>}
                    {item.done ? <>&nbsp; &nbsp;<Chip label='done' color="success" size="small" onClick={() => handleCompleteGoal(item, index)}/></> : <>&nbsp; &nbsp;<Chip  label='not done' color="error" size="small" onClick={() => handleCompleteGoal(item, index)} />
                    <UpdateGoalsModal 
                      item={item} 
                      index={index}
                      onTaskEdited={() => showGoalTaskNotificationMessage('Task has been edited!')}
                    />
                    <RemoveTasksModal 
                      index={index}
                      onTaskDeleted={() => showGoalTaskNotificationMessage('Task has been deleted!')}
                    />
                    </>

    }
    </Box>
                  </div>
                );
              })}
              </div>
              
              <AddTasksModal onTaskAdded={() => showGoalTaskNotificationMessage('Task has been added!')} />
            </AccordionDetails>
          </FastRainbowAccordion>
        </div>
      :    <div>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
         {goalName}
        </AccordionSummary>
        <AccordionDetails>

        Goal for week {goalName.split('-')[1]}
          <br />
          Notes:{notes || 'None'}
          <br />
          Task(s):
          <br/><br/>
          <div style={{display:'flex',flexDirection:'column'}} >
          {tasks?.map((item, index) => {
            return (
              <div style={{display:'flex'}}>
              <Box sx={{ boxShadow: 3 }} style={{display: 'flex', padding:'10px',alignItems:'center',margin:'10px',borderRadius:'50px'}}>

                {item.name}
                {item.optional?'':'*'}
                {item.optional? <>&nbsp; &nbsp;<Chip label='optional' color="success" size="small"/></> : <>&nbsp; &nbsp;<Chip  label='required' color="error" size="small"/></>}
                {item.done ? <>&nbsp; &nbsp;<Chip label='done' color="success" size="small" /></> : <>&nbsp; &nbsp;<Chip  label='not done' color="error" size="small" />
                </>

    }
    </Box>
              </div>
            );
          })}
          </div>
          
        </AccordionDetails>
      </Accordion>
    </div>}
    </>
  );
};
